import React from 'react';
import styled, { css } from 'styled-components';

import { styledTypography } from '@/primitives';

import { GRID_GUTTER_DESKTOP, GRID_GUTTER_LAPTOP, PROFILE_WIDTH_DESKTOP, PROFILE_WIDTH_LAPTOP } from '../../config';
import { LIQUID_ID_ASPECT_RATIO } from '../../LiquidIDSection/config';

import LiquidIDCube from '../../LiquidIDSection/LiquidIDCanvas';
import { PUBLISH_BAR_HEIGHT } from '../../styles';
import { MAIN_NAVIGATION_DESKTOP_HEIGHT } from '@/components/MainNavigation/styles';

export const TOS_TOTAL_HEIGHT = 83;

interface Props {
    $isEdit?: boolean;
    $editingLiquidID?: boolean;
    $liquidIDExpanded?: boolean;
    $newNav?: boolean;
}

export const ProfileWrapper = styled.div<Props>`
    display: grid;
    grid-template-rows: auto 1fr auto;
    line-height: 1;
    // pointer-events: none;
    transition: opacity ${({ theme }) => `${theme.transitions.speeds.normal}s ${theme.transitions.easesCss.cubic}`};

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletSmall}) {
        flex: none;
        position: sticky;
        top: 0;
        left: 0;

        ${({ $isEdit, $newNav }) => {
            if ($isEdit) {
                return css`
                    height: ${$newNav
                        ? `calc(100vh - ${PUBLISH_BAR_HEIGHT}px - ${MAIN_NAVIGATION_DESKTOP_HEIGHT}px )`
                        : `calc(100vh - ${PUBLISH_BAR_HEIGHT + TOS_TOTAL_HEIGHT}px )`};
                `;
            } else {
                return css`
                    height: ${$newNav
                        ? `calc(100vh - ${MAIN_NAVIGATION_DESKTOP_HEIGHT}px )`
                        : `calc(100vh - ${TOS_TOTAL_HEIGHT}px )`};
                `;
            }
        }}

        padding-left: ${GRID_GUTTER_LAPTOP}px;
        padding-right: ${GRID_GUTTER_LAPTOP}px;
        width: ${PROFILE_WIDTH_LAPTOP}px;

        ${({ $newNav }) =>
            !$newNav
                ? css`
                      height: calc(100vh);
                  `
                : css`
                      height: calc(100vh - 100px);
                  `}

        min-height: 800px;
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
        padding-left: ${GRID_GUTTER_DESKTOP}px;
        padding-right: ${GRID_GUTTER_DESKTOP}px;

        width: ${PROFILE_WIDTH_DESKTOP}px;
    }

    ${({ $liquidIDExpanded }) =>
        $liquidIDExpanded &&
        css`
            opacity: 0;
        `}

    ${({ $newNav, $liquidIDExpanded }) =>
        !$newNav &&
        !$liquidIDExpanded &&
        css`
            padding-top: 60px;
        `}
`;

export const ContentWrapper = styled.div<Props>`
    ${({ $isEdit }) =>
        $isEdit &&
        css`
            transition-property: opacity, padding-top;
            transition-duration: ${({ theme }) => theme.transitions.speeds.normal}s;
            transition-timing-function: ${({ theme }) => theme.transitions.easesCss.cubic};
        `}

    ${({ $editingLiquidID, theme }) =>
        $editingLiquidID &&
        css`
            opacity: 0;

            @media (min-width: ${theme.breakpoints.tabletSmall}) {
                /* Offset publish bar animating out and keep content at same position */
                padding-top: ${PUBLISH_BAR_HEIGHT * 2}px;
            }
        `};
`;

interface LiquidIDPositionProps {
    $desktop?: boolean;
    $editingLiquidID?: boolean;
    $preview?: boolean;
}

export const LiquidIDPosition = styled((props) => <LiquidIDCube {...props} />)<LiquidIDPositionProps>`
    aspect-ratio: ${LIQUID_ID_ASPECT_RATIO};

    ${({ $desktop }) =>
        $desktop
            ? css`
                  margin-left: -65px;
                  transform: translateY(0vh);

                  @media all and (display-mode: fullscreen) {
                      transform: translateY(-2vh);
                  }
              `
            : css`
                  max-width: 600px;
                  width: 100%;
                  margin: 0 auto;
              `};

    ${({ $preview, $desktop }) =>
        $preview &&
        ($desktop
            ? css`
                  width: 50vw;
                  top: 0;
                  bottom: 0;
                  margin: auto 0;
                  right: 150px;
                  left: auto;
              `
            : css`
                  top: unset;
                  bottom: 20vh;
              `)}
`;

export const Footer = styled.div<Props>`
    && {
        align-items: flex-end;
        margin-bottom: 48px;
        display: none;

        @media (min-width: ${({ theme }) => theme.breakpoints.tabletSmall}) {
            display: ${({ $editingLiquidID }) => ($editingLiquidID ? 'none' : 'flex')};
        }
    }
`;

export const UsernameErrorWrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 4px;
    padding: 6px 8px;
    color: ${({ theme }) => theme.shades.red[80]};
`;
export const UsernameError = styled.p`
    ${styledTypography.allianceBase};
    color: ${({ theme }) => theme.shades.red[80]};
    font-size: 14px;
    letter-spacing: -0.6px;
    user-select: none;
`;

export const UsernameErrorTooltipText = styled.div`
    white-space: pre;
`;
